import { createInjectionState } from '@vueuse/core';
import { register } from 'swiper/element/bundle';
import type { Swiper } from 'swiper/types';
import { nextTick, onMounted, onUnmounted, ref } from 'vue';
import type { CarouselApi, CarouselEmits, CarouselProps } from './interface';

register();

const [useProvideCarousel, useInjectCarousel] = createInjectionState(
  ({ opts = {}, orientation = 'horizontal' }: CarouselProps, emits: CarouselEmits) => {
    const carouselRef = ref<HTMLElement>();
    const currentIndex = ref(0);
    const itemsCount = ref(0);
    const canScrollNext = ref(false);
    const canScrollPrev = ref(false);
    const swiperInstance = ref<Swiper>();
    const isInitialized = ref(false);

    const getSwiperOptions = () => {
      const flattenOpts: Record<string, unknown> = {
        direction: orientation,
        ...opts,
      };

      return flattenOpts;
    };

    const updateState = () => {
      if (!swiperInstance.value) return;
      const swiper = swiperInstance.value;
      currentIndex.value = swiper.activeIndex || 0;
      itemsCount.value = swiper.slides?.length || 0;
      canScrollPrev.value = !swiper.isBeginning;
      canScrollNext.value = !swiper.isEnd;
    };

    const scrollTo = (index: number) => {
      if (swiperInstance.value) {
        swiperInstance.value.slideTo(index);
      }
      return index;
    };

    const scrollPrev = () => {
      if (swiperInstance.value) {
        swiperInstance.value.slidePrev();
      }
      return Math.max(currentIndex.value - 1, 0);
    };

    const scrollNext = () => {
      if (swiperInstance.value) {
        swiperInstance.value.slideNext();
      }
      return Math.min(currentIndex.value + 1, itemsCount.value - 1);
    };

    const scrollPrePage = (): number => {
      if (!swiperInstance.value) return currentIndex.value;
      let perView = 0;
      if (typeof opts.slidesPerView === 'number') {
        perView = opts.slidesPerView;
      } else {
        perView = swiperInstance.value.slidesPerViewDynamic();
      }
      const index = Math.max(currentIndex.value - perView, 0);
      swiperInstance.value.slideTo(index);
      return index;
    };

    const scrollNextPage = (): number => {
      if (!swiperInstance.value) return currentIndex.value;
      let perView = 0;
      if (typeof opts.slidesPerView === 'number') {
        perView = opts.slidesPerView;
      } else {
        perView = swiperInstance.value.slidesPerViewDynamic();
      }
      const index = Math.min(currentIndex.value + perView, itemsCount.value - 1);
      swiperInstance.value.slideTo(index);
      return index;
    };

    const api: CarouselApi = {
      scrollTo,
      scrollNext,
      scrollPrev,
      scrollNextPage,
      scrollPrePage,
      canScrollNext: () => canScrollNext.value,
      canScrollPrev: () => canScrollPrev.value,
      selectedIndex: () => currentIndex.value,
      scrollSnapList: () => Array.from({ length: itemsCount.value }, (_, i) => i),
      swiper: swiperInstance,
    };

    const carouselApi = ref<CarouselApi>(api);

    const initSwiper = async () => {
      await nextTick();

      const swiperContainer = carouselRef.value?.querySelector('swiper-container');

      if (swiperContainer) {
        const swiperOptions = getSwiperOptions();

        Object.assign(swiperContainer, swiperOptions);
        swiperContainer.initialize();

        await nextTick();

        swiperInstance.value = swiperContainer.swiper;

        if (swiperInstance.value) {
          swiperInstance.value.on('slideChange', () => {
            updateState();
            emits('select', currentIndex.value);
          });
          swiperInstance.value.on('reachBeginning', () => {
            updateState();
          });
          swiperInstance.value.on('reachEnd', () => {
            updateState();
          });
          updateState();
          isInitialized.value = true;
          emits('init-api', api);
        }
      }
    };

    onMounted(() => {
      initSwiper();
    });

    onUnmounted(() => {
      if (swiperInstance.value) {
        swiperInstance.value.destroy();
      }
    });

    return {
      carouselRef,
      carouselApi,
      canScrollPrev,
      canScrollNext,
      scrollPrev,
      scrollNext,
      scrollTo,
      scrollNextPage,
      scrollPrePage,
      orientation,
      currentIndex,
      itemsCount,
      isInitialized,
    };
  },
);

function useCarousel() {
  const carouselState = useInjectCarousel();

  if (!carouselState) throw new Error('useCarousel must be used within a <Carousel />');

  return carouselState;
}

export { useCarousel, useProvideCarousel };
